// Centralized URL utilities for handling API and media URLs

/**
 * API URL with /api suffix - used for API calls.
 * Must be configured through NEXT_PUBLIC_API_URL.
 */
export function getApiUrl(): string {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL?.trim();
  if (!apiUrl) {
    throw new Error('NEXT_PUBLIC_API_URL is not defined. Configure it in your environment file.');
  }
  return apiUrl.replace(/\/$/, '');
}

/**
 * API URL for server-side fetches (SSR, route handlers).
 * API_INTERNAL_URL targets the backend directly (e.g. http://127.0.0.1:4000/api):
 * going through the public domain puts every visitor behind the server's single IP
 * and exhausts the rate limit.
 */
export function getServerApiUrl(): string {
  const internalUrl = typeof window === 'undefined' ? process.env.API_INTERNAL_URL?.trim() : undefined;
  return internalUrl ? internalUrl.replace(/\/$/, '') : getApiUrl();
}

// Kept for backward compatibility in existing imports
export const API_URL = getApiUrl();

// Backend URL without /api suffix - used for serving static files (uploads)
// The backend serves static files from the root, not from /api
export const BACKEND_URL = (() => {
  const apiUrl = getApiUrl();
  // Remove trailing /api or /api/ from the URL
  return apiUrl.replace(/\/api\/?$/, '');
})();

/**
 * Get the full URL for a media file (image, video, document)
 * @param path - The relative path like /uploads/images/2026/01/file.png
 * @returns The full URL like https://api.cerad-ci.org/uploads/images/2026/01/file.png
 */
export function getMediaUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('data:')) return path;
  
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${BACKEND_URL}${normalizedPath}`;
}

/**
 * Transform relative URLs in HTML content to absolute URLs
 * Used for rendering rich text content with embedded media
 * @param html - HTML content with potentially relative URLs
 * @returns HTML content with absolute URLs
 */
export function transformContentUrls(html: string): string {
  if (!html) return '';
  
  // Transform video src attributes
  let transformed = html.replace(
    /(<video[^>]*\s+src=["'])(?!http|data:)([^"']+)(["'][^>]*>)/gi,
    (match, prefix, url, suffix) => `${prefix}${getMediaUrl(url)}${suffix}`
  );
  
  // Transform img src attributes
  transformed = transformed.replace(
    /(<img[^>]*\s+src=["'])(?!http|data:)([^"']+)(["'][^>]*>)/gi,
    (match, prefix, url, suffix) => `${prefix}${getMediaUrl(url)}${suffix}`
  );
  
  // Transform iframe src for local videos/content
  transformed = transformed.replace(
    /(<iframe[^>]*\s+src=["'])(?!http|data:)([^"']+)(["'][^>]*>)/gi,
    (match, prefix, url, suffix) => `${prefix}${getMediaUrl(url)}${suffix}`
  );
  
  // Transform source src attributes (for video/audio sources)
  transformed = transformed.replace(
    /(<source[^>]*\s+src=["'])(?!http|data:)([^"']+)(["'][^>]*>)/gi,
    (match, prefix, url, suffix) => `${prefix}${getMediaUrl(url)}${suffix}`
  );
  
  // Transform poster attributes on video tags
  transformed = transformed.replace(
    /(<video[^>]*\s+poster=["'])(?!http|data:)([^"']+)(["'][^>]*>)/gi,
    (match, prefix, url, suffix) => `${prefix}${getMediaUrl(url)}${suffix}`
  );
  
  return transformed;
}
